import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useEffect } from 'react';
import '../css/App.output.css';
import client from '../wf/client';
import { ApolloProvider } from '@apollo/client';
import ClientInfo from './ClientInfo';
import { i18nManager } from '@withfriendsco/wf-components';
import { MixpanelProvider, useMixpanel } from 'react-mixpanel-browser';
import { PostHogProvider } from 'posthog-js/react';
import { V1ProviderHack } from '@withfriendsco/wf-components';
import { Provider as ShopifyProvider } from "@shopify/app-bridge-react";
import { BrowserRouter as Router, useNavigate, useLocation } from "react-router-dom";
import { I18nContext } from '@shopify/react-i18n';
import { AppProvider } from "@shopify/polaris";
import translations from '@shopify/polaris/locales/en.json';
import { useI18n } from '@shopify/react-i18n';
import { Provider as RollbarProvider, ErrorBoundary as RollbarErrorBoundary } from '@rollbar/react';
const rollbarConfig = {
    accessToken: '91124c51aa0c42968e3378eee144e8df',
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: process.env.NODE_ENV === "development" ? "development" : "production",
};
const App = ({ id, apiKey, host, routeBasename }) => {
    var _a;
    return (_jsx(RollbarProvider, { config: rollbarConfig, children: _jsx(RollbarErrorBoundary, { fallbackUI: () => _jsx("div", { children: "Something went wrong, and our team has been alerted. Try refreshing, or reach out to support@withfriends.co for help" }), children: _jsxs(ApolloProvider, { client: client, children: [_jsx("script", { children: "UPLOADCARE_LOCALE = \"en\"; UPLOADCARE_TABS = \"file url instagram facebook gphotos dropbox gdrive\"; UPLOADCARE_PUBLIC_KEY = \"ff137f8b093066d5ee4d\"; UPLOADCARE_LIVE = false; UPLOADCARE_MANUAL_START = true;" }), _jsx("script", { async: true, src: "https://withfriends.co/jelly/javascript/libraries/uploadcare/uploadcare.full.min.js" }), _jsx(PostHogProvider, { apiKey: (_a = process.env.REACT_APP_PUBLIC_POSTHOG_KEY) !== null && _a !== void 0 ? _a : "development", options: Object.assign({ session_recording: {
                                maskAllInputs: true, // Important - this needs to be true for the below function to be called
                                maskInputFn: (text, element) => {
                                    // List of selectors to mask
                                    const selectorsToMask = [
                                        'input[type=password]',
                                        'input[type=hidden]',
                                        '[autocomplete^=cc-]',
                                        'input[name="Credit_Card_Number"]',
                                        'input[name="Expiration_Month"]',
                                        'input[name="Expiration_Year"]',
                                        'input[name="Card_Verification_Code"]',
                                        'input[name="Routing_Number"]',
                                        'input[name="Account_Number"]'
                                    ];
                                    // Check if the element matches any of the selectors
                                    for (let selector of selectorsToMask) {
                                        if (element.matches(selector)) {
                                            // If it matches, mask it with asterisks
                                            return '*'.repeat(text.length);
                                        }
                                    }
                                    // If it doesn't match any selector, don't mask it
                                    return text;
                                },
                            } }, (!process.env.REACT_APP_PUBLIC_POSTHOG_KEY ? {
                            autocapture: false,
                            loaded: function (ph) { ph.opt_out_capturing(); }
                        } : {})), children: _jsx(MixpanelProvider, { token: process.env.REACT_APP_MIXPANEL_KEY, children: _jsx(I18nContext.Provider, { value: i18nManager(), children: id ?
                                    _jsxs(Router, { basename: routeBasename, children: [_jsx("link", { rel: "stylesheet", href: "https://unpkg.com/@shopify/polaris/build/esm/styles.css" }), _jsx(AppInner, { id: id, apiKey: apiKey })] })
                                    :
                                        _jsx(ClientInfo, { businessId: null }) }) }) })] }) }) }));
};
const AppInner = ({ id, apiKey }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const mixpanel = useMixpanel();
    const [host, setHost] = useState(null);
    if (!host && !!new URLSearchParams(location.search).get("host"))
        setHost(new URLSearchParams(location.search).get("host"));
    const config = {
        apiKey: apiKey,
        host: host,
        forceRedirect: true,
    };
    const history = useMemo(() => ({ replace: (path) => navigate(path, { replace: true }) }), [navigate]);
    const router = useMemo(() => ({
        location,
        history,
    }), [location, history]);
    const [i18n] = useI18n({
        id: 'Polaris',
        fallback: translations,
        translations(locale) {
            return import(
            /* webpackChunkName: "Polaris-i18n", webpackMode: "lazy-once" */ `@shopify/polaris/locales/${locale}.json`).then((dictionary) => dictionary && dictionary.default);
        },
    });
    useEffect(() => {
        if (!mixpanel)
            return;
        // Tracks users who come to this site when they already have a cookie set on withfriends.co
        if (mixpanel.get_distinct_id().indexOf("@") === -1) {
            document.cookie = `wf-lm-id=${mixpanel.get_distinct_id()};domain=.withfriends.co;secure;path=/`;
        }
    }, [mixpanel]);
    return (apiKey ? (host ? (_jsx(AppProvider, { i18n: i18n.translations, children: _jsx(ShopifyProvider, { config: config, router: router, children: _jsx(V1ProviderHack, { apiKey: apiKey, host: host, router: router, children: _jsx(ClientInfo, { businessId: id, isEmbedded: true }) }) }) })) : (_jsx(ClientInfo, { businessId: id }))) : (_jsx(ClientInfo, { businessId: id })));
};
export default App;
